import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CircularProgress,
  LinearProgress,
  Chip,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import QuizIcon from '@mui/icons-material/Quiz';
import NoteIcon from '@mui/icons-material/Note';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import RefreshIcon from '@mui/icons-material/Refresh';
import BarChart from '../../components/profile/BarChart';
import { fetchLearningStats } from '../../api/learningStats';

// 학습 통계 컴포넌트
const LearningStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalTerms: 0,
    wrongAnswers: 0,
    customTerms: 0,
    customDefinitions: 0,
    studyTime: 0
  });
  const [userStats, setUserStats] = useState(null);
  const [recentStudyDays, setRecentStudyDays] = useState(Array(7).fill(0));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // 통계 데이터 가져오는 함수
  const fetchStats = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // 오답노트에 저장된 항목 수 (wrong_answers 테이블)
      const { data: wrongData, error: wrongError } = await supabase
        .from('wrong_answers')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id);
        
      // 사용자 용어 수
      const { data: termsData, error: termsError } = await supabase
        .from('my_terms')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id);
        
      // 사용자 정의 수
      const { data: defsData, error: defsError } = await supabase
        .from('my_definitions')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id);
        
      setStats({
        wrongAnswers: wrongData?.length || 0,  // 이제 이건 "오답노트 용어 수"로 사용됨
        customTerms: termsData?.length || 0,
        customDefinitions: defsData?.length || 0,
        totalTerms: 0,
        studyTime: 0
      });
    } catch (error) {
      console.error('통계 데이터 가져오기 실패:', error);
    }
  }, [user]);

  // 확장된 학습 통계 데이터 가져오기
  const fetchExtendedStats = useCallback(async () => {
    if (!user) return;
    
    try {
      // API를 통해 사용자 학습 통계 가져오기
      const data = await fetchLearningStats();
      
      if (data) {
        setUserStats(data);
        
        // 최근 학습 일자 데이터 처리
        try {
          let recentDays;
          
          if (typeof data.recent_study_days === 'string') {
            recentDays = JSON.parse(data.recent_study_days);
          } else if (Array.isArray(data.recent_study_days)) {
            recentDays = data.recent_study_days;
          } else {
            recentDays = Array(7).fill(0);
          }
          
          setRecentStudyDays(recentDays);
        } catch (e) {
          console.warn('최근 학습 데이터 파싱 오류:', e);
          setRecentStudyDays(Array(7).fill(0));
        }
      }
    } catch (error) {
      console.error('확장 통계 데이터 가져오기 실패:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);
  
  // 데이터 새로고침
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStats();
    await fetchExtendedStats();
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchStats();
    fetchExtendedStats();
  }, [fetchStats, fetchExtendedStats]);

  // 탭 변경 핸들러
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading && !refreshing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  // 정답률 계산
  const correctRate = userStats?.total_questions
    ? Math.round((userStats.correct_count / userStats.total_questions) * 100)
    : 0;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">학습 통계</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={handleRefresh} 
            disabled={refreshing}
            size="small"
          >
            {refreshing ? '새로고침 중...' : '통계 새로고침'}
          </Button>
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="학습 통계" />
          <Tab label="학습 활동 그래프" />
        </Tabs>
      </Box>

      {tabValue === 0 ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <QuizIcon color="primary" />
                  <Typography variant="subtitle2">총 문제 수</Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>{userStats?.total_questions || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="subtitle2">맞은 문제 수</Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>{userStats?.correct_count || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <ErrorOutlineIcon color="error" />
                  <Typography variant="subtitle2">오답 수</Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>{userStats?.wrong_count || 0}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  퀴즈에서 틀린 모든 문제의 총합
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="subtitle2">정답률</Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>{correctRate}%</Typography>
                <LinearProgress variant="determinate" value={correctRate} sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <NoteIcon color="info" />
                  <Typography variant="subtitle2">오답노트 용어 수</Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>{stats.wrongAnswers}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  사용자가 직접 저장한 오답만 카운트
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <BookmarkIcon color="primary" />
                  <Typography variant="subtitle2">나만의 용어 수</Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>{stats.customTerms}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <AutoStoriesIcon color="secondary" />
                  <Typography variant="subtitle2">나만의 정의 수</Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>{stats.customDefinitions}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <AccessTimeIcon color="info" />
                  <Typography variant="subtitle2">총 퀴즈학습 시간</Typography>
                </Box>
                <Typography variant="h5" sx={{ mt: 1 }}>{userStats?.total_study_time ? `${userStats.total_study_time}분` : '0분'}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  퀴즈 활동 중 측정된 시간만 반영
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>최근 7일 학습 추이</Typography>
                <BarChart data={recentStudyDays} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default LearningStats; 